import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Copy,
  ExternalLink,
  RefreshCcw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

type CheckStatus = "pending" | "testing" | "passed" | "blocked";

type ChecklistItem = {
  id: string;
  section: string;
  title: string;
  owner: string;
  expected: string;
  action: string;
};

type SavedItem = {
  status: CheckStatus;
  notes: string;
};

type SavedRun = {
  tester: string;
  lastSaved: string;
  items: Record<string, SavedItem>;
};

const STORAGE_KEY = "bsa:production-readiness:testal:2026-06-21";

const statusStyles: Record<CheckStatus, string> = {
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  testing: "bg-blue-50 text-blue-700 border-blue-200",
  passed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blocked: "bg-red-50 text-red-700 border-red-200",
};

const checklist: ChecklistItem[] = [
  {
    id: "square-oauth",
    section: "Access",
    title: "Confirm Square OAuth access",
    owner: "Developer",
    expected: "Merchant Testal Mexican Kitchen is connected. Location is L6BPGX3NVXNC8.",
    action: "Open Square API test output or run the Square connection check before taking payment.",
  },
  {
    id: "square-reports",
    section: "Access",
    title: "Confirm sales/order visibility",
    owner: "Developer",
    expected: "We can read payments, orders, catalog, customers, and locations through Square API.",
    action: "Verify monthly revenue report and recent orders exist.",
  },
  {
    id: "menu-reconciliation",
    section: "Menu",
    title: "Review menu reconciliation exceptions",
    owner: "Developer + Rodo",
    expected: "Alcohol stays POS-only. Square-only non-alcohol and website-only mapping items are listed for Fernando review.",
    action: "Use SQUARE_MENU_ACTION_ANALYSIS_2026-06-21.md and mark launch blockers only.",
  },
  {
    id: "site-smoke",
    section: "Website",
    title: "Smoke test internal website",
    owner: "Developer",
    expected: "Menu, cart, checkout, admin, and settings load on testal-app.vercel.app.",
    action: "Open the internal site, add at least two items with modifiers, and reach checkout.",
  },
  {
    id: "discount",
    section: "Checkout",
    title: "Verify internal test discount",
    owner: "Rodo",
    expected: "Code BSAINTERNAL25 applies 25% off and is limited to internal testing.",
    action: "Apply the discount in checkout before live payment test.",
  },
  {
    id: "live-payment",
    section: "Checkout",
    title: "Run one small live payment",
    owner: "Rodo",
    expected: "Payment succeeds with production Square app and location L6BPGX3NVXNC8.",
    action: "Use a real card for a small order. Do not send Fernando/customer notifications yet.",
  },
  {
    id: "square-order",
    section: "Square",
    title: "Confirm Square order lands correctly",
    owner: "Rodo + Fernando",
    expected: "Square shows item names, customer info, pickup fulfillment, and total correctly.",
    action: "Ask Fernando to open Square Orders/POS and confirm what he sees.",
  },
  {
    id: "printer",
    section: "Square",
    title: "Confirm printer/ticket behavior",
    owner: "Fernando",
    expected: "Kitchen ticket prints with item names and modifiers. No duplicate tickets unless reprint is pressed.",
    action: "Run the test at the restaurant with the actual Square printer setup.",
  },
  {
    id: "notifications",
    section: "Notifications",
    title: "Verify internal notifications",
    owner: "Developer",
    expected: "Admin alert email and SMS test override go only to BSA during internal testing.",
    action: "Confirm Rodo receives success/failure notifications after test order.",
  },
  {
    id: "ops-board",
    section: "Operations",
    title: "Test admin order board",
    owner: "Rodo",
    expected: "New order appears with sound, timer, acknowledge/accept/ready/complete, and reprint controls.",
    action: "Open the Testal admin board and walk through the order lifecycle.",
  },
  {
    id: "ipad-app",
    section: "Operations",
    title: "Test iPad ops flow",
    owner: "Developer + Fernando",
    expected: "iPad app points to testal-app.vercel.app and can show loud new-order alert.",
    action: "Use the web admin as fallback if TestFlight/iPad is not ready tonight.",
  },
  {
    id: "refund",
    section: "Payments",
    title: "Run refund test",
    owner: "Rodo",
    expected: "Square refund can be initiated and matched back to the test payment.",
    action: "Refund the small test payment after Square/POS/printer checks are complete.",
  },
  {
    id: "dns",
    section: "Launch",
    title: "Confirm DNS launch path",
    owner: "Developer",
    expected: "Public domain remains on temporary page until Fernando signs off. Owner.com migration remains separate.",
    action: "Do not point testalphx.com to the ordering app until end-to-end test passes.",
  },
  {
    id: "rollback",
    section: "Launch",
    title: "Confirm rollback plan",
    owner: "Developer",
    expected: "DNS snapshots and current zone records are saved in the project folder.",
    action: "Verify DNS-CUTOVER and restore snapshot files are present.",
  },
  {
    id: "signoff",
    section: "Launch",
    title: "Fernando sign-off",
    owner: "Rodo",
    expected: "Fernando confirms order flow, printer, refunds, prep-time workflow, and public launch timing.",
    action: "Only after sign-off, remove internal-only settings and schedule final domain cutover.",
  },
];

function emptyRun(): SavedRun {
  return {
    tester: "",
    lastSaved: "",
    items: Object.fromEntries(
      checklist.map((item) => [item.id, { status: "pending", notes: "" }])
    ),
  };
}

function statusIcon(status: CheckStatus) {
  if (status === "passed") return <CheckCircle2 className="h-4 w-4" />;
  if (status === "blocked") return <AlertTriangle className="h-4 w-4" />;
  if (status === "testing") return <Clock className="h-4 w-4" />;
  return <ClipboardCheck className="h-4 w-4" />;
}

export default function ProductionReadinessPage() {
  const { toast } = useToast();
  const [run, setRun] = useState<SavedRun>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return emptyRun();
      const parsed = JSON.parse(raw) as SavedRun;
      return {
        ...emptyRun(),
        ...parsed,
        items: { ...emptyRun().items, ...parsed.items },
      };
    } catch {
      return emptyRun();
    }
  });

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...run, lastSaved: new Date().toISOString() })
    );
  }, [run]);

  const stats = useMemo(() => {
    const statuses = checklist.map((item) => run.items[item.id]?.status || "pending");
    const passed = statuses.filter((status) => status === "passed").length;
    const blocked = statuses.filter((status) => status === "blocked").length;
    const testing = statuses.filter((status) => status === "testing").length;
    return {
      passed,
      blocked,
      testing,
      progress: Math.round((passed / checklist.length) * 100),
    };
  }, [run.items]);

  const sections = useMemo(() => {
    return checklist.reduce<Record<string, ChecklistItem[]>>((acc, item) => {
      acc[item.section] = acc[item.section] || [];
      acc[item.section].push(item);
      return acc;
    }, {});
  }, []);

  const updateItem = (id: string, updates: Partial<SavedItem>) => {
    setRun((current) => ({
      ...current,
      items: {
        ...current.items,
        [id]: { ...current.items[id], ...updates },
      },
    }));
  };

  const copySummary = async () => {
    const summary = checklist
      .map((item) => {
        const saved = run.items[item.id];
        return `${saved.status.toUpperCase()} - ${item.title}${saved.notes ? `\nNotes: ${saved.notes}` : ""}`;
      })
      .join("\n\n");

    await navigator.clipboard.writeText(
      `Testal Production Readiness\nTester: ${run.tester || "Not set"}\nProgress: ${stats.progress}%\n\n${summary}`
    );
    toast({ title: "Copied", description: "Checklist summary copied to clipboard." });
  };

  const resetRun = () => {
    setRun(emptyRun());
    toast({ title: "Reset", description: "Local checklist run was reset." });
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5">
      <div className="space-y-4">
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Production Readiness
              </p>
              <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-950">
                Testal Launch Checklist
              </h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Internal only. Use this before any public DNS or customer-facing launch changes.
              </p>
            </div>
            <Badge className={stats.blocked ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}>
              {stats.blocked ? `${stats.blocked} blocked` : "On track"}
            </Badge>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <div className="rounded-md bg-slate-100 p-3 text-center">
              <div className="text-xl font-bold text-slate-950">{stats.progress}%</div>
              <div className="text-xs font-medium text-slate-500">Complete</div>
            </div>
            <div className="rounded-md bg-blue-50 p-3 text-center">
              <div className="text-xl font-bold text-blue-700">{stats.testing}</div>
              <div className="text-xs font-medium text-blue-600">Testing</div>
            </div>
            <div className="rounded-md bg-emerald-50 p-3 text-center">
              <div className="text-xl font-bold text-emerald-700">{stats.passed}</div>
              <div className="text-xs font-medium text-emerald-600">Passed</div>
            </div>
          </div>

          <Progress value={stats.progress} className="mt-4 h-2" />

          <label className="mt-4 block text-xs font-semibold text-slate-600">
            Tester / developer
          </label>
          <input
            value={run.tester}
            onChange={(event) => setRun((current) => ({ ...current, tester: event.target.value }))}
            placeholder="Name"
            className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
          />
        </section>

        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div className="text-sm leading-6 text-amber-900">
              <strong>Rule:</strong> test payments are allowed. Public launch changes are not allowed until
              payment, Square order, printer, notifications, and Fernando sign-off are passed.
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-bold text-slate-950">Quick Links</h2>
          <div className="mt-3 grid gap-2">
            {[
              ["Internal website", "https://testal-app.vercel.app"],
              ["Menu", "https://testal-app.vercel.app/menu"],
              ["Checkout", "https://testal-app.vercel.app/checkout"],
              ["Temporary public page", "https://testalphx.com"],
            ].map(([label, href]) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {label}
                <ExternalLink className="h-4 w-4 text-slate-400" />
              </a>
            ))}
          </div>
        </section>

        {Object.entries(sections).map(([section, items]) => (
          <section key={section} className="rounded-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-4 py-3">
              <h2 className="text-sm font-bold text-slate-950">{section}</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {items.map((item) => {
                const saved = run.items[item.id] || { status: "pending", notes: "" };
                return (
                  <div key={item.id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold leading-5 text-slate-950">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-xs font-medium text-slate-500">
                          Owner: {item.owner}
                        </p>
                      </div>
                      <Badge className={`${statusStyles[saved.status]} flex shrink-0 items-center gap-1 border`}>
                        {statusIcon(saved.status)}
                        {saved.status}
                      </Badge>
                    </div>

                    <div className="mt-3 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                      <p><strong>Expected:</strong> {item.expected}</p>
                      <p className="mt-1"><strong>Action:</strong> {item.action}</p>
                    </div>

                    <div className="mt-3 grid grid-cols-4 gap-2">
                      {(["pending", "testing", "passed", "blocked"] as CheckStatus[]).map((status) => (
                        <button
                          key={status}
                          onClick={() => updateItem(item.id, { status })}
                          className={`rounded-md border px-2 py-2 text-xs font-semibold capitalize ${
                            saved.status === status
                              ? statusStyles[status]
                              : "border-slate-200 bg-white text-slate-500"
                          }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>

                    <Textarea
                      value={saved.notes}
                      onChange={(event) => updateItem(item.id, { notes: event.target.value })}
                      placeholder="Developer feedback, issue, screenshot note, or exact result..."
                      className="mt-3 min-h-20 resize-none text-sm"
                    />
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-bold text-slate-950">Developer Notes</h2>
          <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-600">
            <li>Credentials stay in the approved password manager. Do not paste passwords into this checklist.</li>
            <li>Square is connected through OAuth, so no Fernando Square password is needed for API testing.</li>
            <li>Use BSA internal notifications only until Fernando approves the live customer workflow.</li>
          </ul>
          <div className="mt-4 flex gap-2">
            <Button onClick={copySummary} className="flex-1 gap-2">
              <Copy className="h-4 w-4" />
              Copy Summary
            </Button>
            <Button variant="outline" onClick={resetRun} className="gap-2">
              <RefreshCcw className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
